import React, { useRef, useEffect, useMemo, ReactElement } from "react";
import AreaReadDto from "../../../Models/Area/AreaReadDto";
import { ConsumableReadDto } from "../../../Models/Consumable/ConsumableReadDto";
import { DeviceUpdateDto } from "../../../Models/Device/DeviceUpdateDto";
import useKendoTableState from "../../Shared/Hooks/useKendoTableState";
import { ColumnMetaData } from "../../Shared/Interfaces/ColumnMetaData";
import { DeviceReadTableDto } from "../../../Models/Device/DeviceReadTableDto";
import GBKendoDataCell from "../../Shared/Components/Table/GBKendoDataCell";
import DeviceValidationSchema from "../Utilities/DeviceValidationSchema";
import { CompositeFilterDescriptor, filterBy, orderBy } from "@progress/kendo-data-query";
import { ExcelExport, ExcelExportColumn } from "@progress/kendo-react-excel-export";
import { Grid, GridFilterChangeEvent, GridPageChangeEvent, GridSortChangeEvent, GridToolbar, GridColumn as Column, GridRowProps } from "@progress/kendo-react-grid";
import { isRequiredBySchema } from "../../Shared/Utilities/YupValidationUtilities";
import { Formik } from "formik";
import GBKendoToolbar from "../../Shared/Components/Table/GBKendoToolbar";
import { Checkbox } from "@progress/kendo-react-inputs";
import { CustomDateFilterCell } from "../../Shared/Components/Table/CustomDateFilterCell";
import { CustomTextFilterCell } from "../../Shared/Components/Table/CustomTextFilterCell";
import { useLocalization } from '@progress/kendo-react-intl';
import { enMessages } from "../../../messages/en-US";
import EntityNotesButton from "../../Dashboards/Assets/Components/Notes/EntityNotesButton";
import devicesApiService from "../Utilities/DevicesApiService";
import moment from "moment";
import { formatSmartDate } from "../../Shared/Utilities/DateFormattingUtils";
import {  Tooltip } from "@progress/kendo-react-tooltip";

interface Props {
  handleSubmit: any;
  initialSort: any;
  initialSortDescriptor: any;
  devices: Array<DeviceReadTableDto>;
  consumables: Array<ConsumableReadDto>;
  areas: Array<AreaReadDto>;
  updateDevice: (devices: Array<DeviceUpdateDto>) => void;
  height: number;
}

const initialFilter: CompositeFilterDescriptor = {
  logic: "and",
  filters: [],
};

function DeviceTable(props: Props) {
  const tableState = useKendoTableState({key: "Devices", initialAssets: props.devices });
  
  const stateRef = useRef(tableState);

  const localizationService = useLocalization();

  useEffect(() => {
    stateRef.current = tableState
   }, [tableState]);

  const [filter, setFilter] = React.useState(initialFilter);
  const [sort, setSort] = React.useState(props.initialSortDescriptor ?? props.initialSort);
  const [previousSort, setPreviousSort] = React.useState(sort || props.initialSortDescriptor || props.initialSort);
  const [editModeRows, setEditModeRows] = React.useState<Array<any>>([]);

    // Table columns
  const columns = React.useMemo<Array<ColumnMetaData<DeviceReadTableDto>>>(
    () => [
      {
        header: `${localizationService.toLanguageString('custom.notes', enMessages.custom.notes)}`,
        displayField: "description",
        filterType: "boolean",
        Cell: (cellProps) => (
          <GBKendoDataCell<DeviceReadTableDto>
            className={"text-center"}
            dataFieldName="description"
            cellProps={cellProps}
            fieldAccessor={(x) => (
              <EntityNotesButton
                entityType="Device"
                entityId={x.id}
                entityName={x.serialNumber}
                buttonProps={{ fillMode: "flat" }}
              />
            )}
            isAddMode={false}
            isEditMode={false}
            onCellChanged={tableState.onCellChanged}
          />
        ),
      },
      {
        header: `${localizationService.toLanguageString('custom.location', enMessages.custom.location)}`,
        displayField: "areaName",
        Cell: (cellProps) => (
          <GBKendoDataCell<DeviceReadTableDto>
            placeholder="All Locations"
            dataFieldName="areaId"
            fieldAccessor={(x) => x.areaId}
            displayTextAccessor={(x) => x.name}
            cellProps={cellProps}
            isAddMode={false}
            isEditMode={tableState.canModifyRow}
            onCellChanged={tableState.onCellChanged}
            assets={props.areas}
            inputType="combobox"
            initialDisplayText={cellProps.dataItem.areaName}
          />
        ),
      },
      {
        header: `${localizationService.toLanguageString('custom.consumable', enMessages.custom.consumable)}`,
        displayField: "consumableName",
        Cell: (cellProps) => (
          <GBKendoDataCell<DeviceReadTableDto>
            placeholder="All Consumables"
            dataFieldName="consumableId"
            fieldAccessor={(x) => x.consumableId}
            displayTextAccessor={(x) => x.name}
            cellProps={cellProps}
            isAddMode={false}
            isEditMode={tableState.canModifyRow}
            onCellChanged={tableState.onCellChanged}
            assets={props.consumables}
            inputType="combobox"
            initialDisplayText={cellProps.dataItem.consumableName}
          />
        ),
      },
      {
        header: `${localizationService.toLanguageString('custom.modelNumber', enMessages.custom.modelNumber)}`,
        displayField: "modelNumber",
        filterType: "text",
        Cell: (cellProps) => (
          <GBKendoDataCell<DeviceReadTableDto>
            dataFieldName="modelNumber"
            cellProps={cellProps}
            fieldAccessor={(x) => x.modelNumber}
            isAddMode={false}
            isEditMode={false}
            onCellChanged={tableState.onCellChanged}
          />
        ),
      },
      {
        header: `${localizationService.toLanguageString('custom.serialNumber', enMessages.custom.serialNumber)}`,
        displayField: "serialNumber",
        filterType: "text",
        Cell: (cellProps) => (
          <GBKendoDataCell<DeviceReadTableDto>
            dataFieldName="serialNumber"
            cellProps={cellProps}
            fieldAccessor={(x) => x.serialNumber}
            isAddMode={false}
            isEditMode={false}
            onCellChanged={tableState.onCellChanged}
          />
        ),
      },
      {
        header: `${localizationService.toLanguageString('custom.firmwareVersion', enMessages.custom.firmwareVersion)}`,
        displayField: "firmwareVersion",
        filterType: "text",
        Cell: (cellProps) => (
          <GBKendoDataCell<DeviceReadTableDto>
            dataFieldName="firmwareVersion"
            cellProps={cellProps}
            fieldAccessor={(x) => x.firmwareVersion}
            isAddMode={false}
            isEditMode={false}
            onCellChanged={tableState.onCellChanged}
          />
        ),
      },
      {
        header: `${localizationService.toLanguageString('custom.lastSeen', enMessages.custom.lastSeen)}`,
        displayField: "lastConnected",
        filterType: "text",
        Cell: (cellProps) => (
          <GBKendoDataCell<DeviceReadTableDto>
            dataFieldName="lastConnected"
            cellProps={cellProps}
           fieldAccessor={(x) => {
              const { displayText: lastSeenText, tooltipText: tootipText } = formatSmartDate(
                x.lastConnected,
                localizationService,
                enMessages
              );
              return <div style={{display:'flex', alignItems:'center', }}>
              <Tooltip ref={tooltip} anchorElement="target" position="bottom" openDelay={2} content={(props) => <TooltipContentTemplate title={props.title} />}>
                <div style={{cursor:'pointer'}} title={tootipText} onMouseOver={(event) => tooltip.current && tooltip.current.handleMouseOver(event)}
                onMouseOut={(event) => tooltip.current && tooltip.current.handleMouseOut(event)}>{lastSeenText}</div>
              </Tooltip>
            </div>;
            }}
            isAddMode={false}
            isEditMode={false}
            onCellChanged={tableState.onCellChanged}
          />
        ),
      },
    ],
    [tableState.canModifyRow, tableState.tempRows.length, filter, sort]
  );

    const tooltip = React.useRef<Tooltip>(null);
  const TooltipContentTemplate = (props: any) => {
    return (
      <div>{props.title}</div>
    );
  };

  const _export = React.useRef<ExcelExport | null>(null);
  // Notes are managed outside this table's query, so latestNote in the cache can be stale.
  // On export, fetch fresh devices directly from the API, patch the fresh latestNote onto the
  // already-processed rows, then export that data.
  const excelExport = async () => {
    let exportData = processedData;
    try {
      const fresh = await devicesApiService.getAllDevices();
      const latestNoteById = new Map(fresh.map((d) => [d.id, d.latestNote]));
      exportData = processedData.map((row) =>
        latestNoteById.has(row.id) ? { ...row, latestNote: latestNoteById.get(row.id) } : row
      );
    } catch (e) {
      console.log(e);
    }
    _export.current?.save(exportData);
  };
  const ref = useRef<any>();

  /**The following use effects are required to fix a race condition created by updating formik fields
   * Programatically. They ensure that validation is performed after the values update instead of before.
   **/
  useEffect(() => {
    ref.current.validateForm();
  }, [ref.current?.values]);
  useEffect(() => {
    ref.current.validateForm();
  }, [ref.current]);

  /**
   * Set the skip amount to zero if the number of temp rows (indicating an add row has been added).
   * This effectively ensures the add row is always scrolled into view
   */
  useEffect(() => {
    if (tableState.tempRows.length === 1) {
      setSkip(0);
    }
  }, [tableState.tempRows.length]);

  //The following state is required for dynamic scrolling
  const [skip, setSkip] = React.useState<number>(0);
  const pageChange = (event: GridPageChangeEvent) => {
    setSkip(event.page.skip);
  };

  const isColumnForRequiredField = (displayField: string | undefined): boolean => {
    return (
      isRequiredBySchema(displayField?.replace("Name", "Id"), DeviceValidationSchema) &&
      (tableState.tempRows.length > 0 || tableState.canModifyRow)
    );
  };

  useEffect(() => {
    // Reset skip when filter or sort changes
    setSkip(0);
  }, [filter, sort]);

  // Add this effect to handle edit mode row storage
  useEffect(() => {
    if (tableState.canModifyRow) {
        // Store current sorted rows when entering edit mode
        if (editModeRows.length === 0) {
            const sortedRows = sort ? orderBy(tableState.assets, sort) : tableState.assets;
            setEditModeRows(sortedRows);
        }
    } else {
        setEditModeRows([]);
    }
  }, [tableState.canModifyRow]);

  // Replace the existing processedData implementation
  const processedData = useMemo(() => {
    let initialRows = tableState.assets;  // Always start with tableState.assets

    if (filter) {
      // First get the modified rows
      const modifiedRows = initialRows.filter(row => tableState.modifiedRowIds.includes(row.id));
      
      // Filter the non-modified rows
      const filteredRows = filterBy(
        initialRows.filter(row => !tableState.modifiedRowIds.includes(row.id)),
        filter
      );

      // Combine both sets
      initialRows = [...modifiedRows, ...filteredRows];
    }

    // Use stored order in edit mode, otherwise normal sorting
    if (tableState.canModifyRow && editModeRows.length > 0) {
      const editModeOrder = new Map(editModeRows.map((row, index) => [row.id, index]));
      initialRows.sort((a, b) => (editModeOrder.get(a.id) ?? 0) - (editModeOrder.get(b.id) ?? 0));
    } else if (sort) {
      initialRows = orderBy(initialRows, sort);
    }

    return tableState.tempRows.concat(initialRows);
  }, [tableState.assets, tableState.tempRows, tableState.modifiedRowIds, filter, sort]);

  const initialisedRowData = {
    id: "",
    type: "",
    areaId: "",
    guid: "",
    modelNumber: "",
    serialNumber: "",
    firmwareVersion: "",
    consumableId: "",
    status: "",
    reaName: "",
    consumableName: "",
    areaIds: "",
    description: "",
    lastConnected: "" ,
  };

  return (
    <div>
      <Formik
        innerRef={ref}
        key={props.devices.length}
        onSubmit={props.handleSubmit}
        validationSchema={DeviceValidationSchema}
        initialValues={initialisedRowData}
      >
        {({ submitForm, values, resetForm, isSubmitting }) => (
          <>
          <ExcelExport data={processedData} ref={_export} collapsible={true} fileName={localizationService.toLanguageString('custom.devices', enMessages.custom.devices)}>
            <ExcelExportColumn field="areaName" title={localizationService.toLanguageString('custom.location', enMessages.custom.location)} />
            <ExcelExportColumn field="consumableName" title={localizationService.toLanguageString('custom.consumable', enMessages.custom.consumable)} />
            <ExcelExportColumn field="modelNumber" title={localizationService.toLanguageString('custom.modelNumber', enMessages.custom.modelNumber)} />
            <ExcelExportColumn field="serialNumber" title={localizationService.toLanguageString('custom.serialNumber', enMessages.custom.serialNumber)} />
            <ExcelExportColumn field="firmwareVersion" title={localizationService.toLanguageString('custom.firmwareVersion', enMessages.custom.firmwareVersion)} />
            <ExcelExportColumn field="lastConnected" title={localizationService.toLanguageString('custom.lastSeen', enMessages.custom.lastSeen)} />
            <ExcelExportColumn field="latestNote" title={localizationService.toLanguageString('custom.notes', enMessages.custom.notes)} />
          </ExcelExport>
          <Grid
              key={tableState.tempRows.length.toString()}
              rowHeight={46}
              pageSize={30}
              total={processedData.length}
              data={processedData}
              scrollable="virtual"
              skip={skip}
              onPageChange={pageChange}
              filterable={true}
              filter={filter}
              onFilterChange={(e: GridFilterChangeEvent) => setFilter(e.filter)}
              sortable={!tableState.canModifyRow}
              sort={tableState.canModifyRow ? previousSort : sort}
              onSortChange={(e: GridSortChangeEvent) => {
                if (!tableState.canModifyRow) {
                    setSort(e.sort);
                    setPreviousSort(e.sort);
                }
              }}
              style={{
                height: props.height,
              }}
              className="w-full  z-0"
              //Custom render row,
              rowRender={(trElement: ReactElement, props: GridRowProps) => {
                let baseStyle = { ...trElement.props.style };
                if (tableState.modifiedRowIds.includes(props.dataItem.id)) {
                  baseStyle.backgroundColor = "rgba(255, 252, 88, 0.25)";
                }
                if (tableState.selectedRowsIds.includes(props.dataItem.id)) {
                  baseStyle.backgroundColor = "rgba(255, 99, 88, 0.25)";
                }

                return React.cloneElement(
                  trElement,
                  {
                    className: trElement.props.className,
                    style: baseStyle,
                    onClick: trElement.props.onClick,
                    onDoubleClick: trElement.props.onDoubleClick,
                    onContextMenu: trElement.props.onContextMenu,
                    role: trElement.props.role,
                    'aria-rowindex': trElement.props['aria-rowindex'],
                    'data-grid-row-index': trElement.props['data-grid-row-index']
                  },
                  trElement.props.children
                );
              }}
            >
              <GridToolbar>  
                <GBKendoToolbar
                  name={localizationService.toLanguageString('custom.devices', enMessages.custom.devices)}
                  tableState={tableState}
                  exportAction={excelExport}
                  importAction={null}
                  addAction={null}
                  updateAction={
                    props.updateDevice
                      ? async () => {
                          let updatedAssets = tableState.assets.filter((x) => tableState.modifiedRowIds.includes(x.id));
                          await props.updateDevice(updatedAssets);
                        }
                      : null
                  }
                  deleteAction={null}
                  discardChanges={() => tableState.resetState()}
                />
              </GridToolbar>

              {/* Selection - only if in edit mode*/}
              {tableState.canModifyRow && (
                <Column  
                  key={"Selection"}
                  cell={(props) => (
                    <td>
                        <Checkbox
                          onChange={() => {
                            if (tableState.selectedRowsIds.includes(props.dataItem.id)) {
                              tableState.setSelectedRowIds(
                                tableState.selectedRowsIds.filter((id) => id !== props.dataItem?.id)
                              );
                            } else {
                              tableState.setSelectedRowIds([...tableState.selectedRowsIds, props.dataItem?.id]);
                            }
                          }}
                          checked={tableState.selectedRowsIds.includes(props.dataItem.id)}
                        />
                    </td>
                  )}
                  width="50px"
                  filterable={false}
                  sortable={false} />)}

               {/* From Column Array */}
               {columns.map((column, index) => {
                if (column.displayField === "description") {
                  return (
                    //Notes column — fixed width, consistent across all account-setup tables.
                    <Column
                      width="65px"
                      className="text-center"
                      key={index}
                      cell={column.Cell}
                      title={`${column.header}${isColumnForRequiredField(column.displayField) ? "*" : ""}`}
                      field={column.displayField}
                      filterable={false}
                      sortable={true}
                    />
                  );
                }
                return (
                  //Create wrapper/HOC with editable cell and add cell definitions,
                  <Column
                    width="auto"
                    key={index}
                    cell={column.Cell}
                    // Show asterix in header if required TODO: pretty hacky - assumes that the name of the data field is the same as the display field with Name replaced.
                    title={`${column.header}${isColumnForRequiredField(column.displayField) ? "*" : ""}`}
                    field={column.displayField}
                    filter={column.filterType}
                    filterCell={column.filterType === "date" ? CustomDateFilterCell : CustomTextFilterCell}
                  />
                );
              })}
            </Grid>
          </>
        )}
      </Formik>
    </div> 
  );
};

export default DeviceTable;